import * as React from "react";
import {
    Box, Paper, Stack, Typography, TextField, Button, IconButton, Divider, Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { adminFetch } from "../../services/adminFetch";

type FeatureRow = { id: number; name: string };

export default function AdminFeaturesPage() {
    const [items, setItems] = React.useState<FeatureRow[]>([]);
    const [q, setQ] = React.useState("");
    const [name, setName] = React.useState("");
    const [err, setErr] = React.useState<string | null>(null);
    const [ok, setOk] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);

    const load = React.useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await adminFetch("/api/admin/features");
            const d = await r.json().catch(() => ({}));
            setItems(Array.isArray(d.items) ? d.items : []);
        } catch {
            setErr("Αποτυχία φόρτωσης.");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const create = async () => {
        setErr(null); setOk(null);
        const n = name.trim();
        if (!n) return setErr("Γράψε όνομα feature.");
        try {
            const r = await adminFetch("/api/admin/features", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: n }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok || !d.ok) return setErr(d.error || "Αποτυχία δημιουργίας.");
            setName("");
            setOk("Προστέθηκε.");
            await load();
        } catch {
            setErr("Αποτυχία δημιουργίας.");
        }
    };

    const del = async (id: number) => {
        setErr(null); setOk(null);
        try {
            const r = await adminFetch(`/api/admin/features/${id}`, { method: "DELETE" });
            const d = await r.json().catch(() => ({}));
            if (!r.ok || !d.ok) return setErr(d.error || "Αποτυχία διαγραφής.");
            setOk("Διαγράφηκε.");
            setItems((s) => s.filter((x) => x.id !== id));
        } catch {
            setErr("Αποτυχία διαγραφής.");
        }
    };

    const filtered = items.filter((x) =>
        x.name.toLowerCase().includes(q.trim().toLowerCase())
    );

    return (
        <Stack spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
                Admin · Features
            </Typography>

            {err ? <Alert severity="error">{err}</Alert> : null}
            {ok ? <Alert severity="success">{ok}</Alert> : null}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                        label="New feature"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                    />
                    <Button variant="contained" sx={{ fontWeight: 900 }} onClick={create}>
                        Add
                    </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <TextField
                    label="Search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    fullWidth
                />

                <Box sx={{ mt: 2 }}>
                    {loading ? (
                        <Typography>Φόρτωση…</Typography>
                    ) : filtered.length === 0 ? (
                        <Typography color="text.secondary">Κανένα αποτέλεσμα.</Typography>
                    ) : (
                        <Stack spacing={1}>
                            {filtered.map((x) => (
                                <Paper key={x.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {x.name}
                                        </Typography>
                                        <IconButton color="error" onClick={() => del(x.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    )}
                </Box>
            </Paper>
        </Stack>
    );
}
